/**
 * Tags — a Library document's tags (a file's, a synced document's, an
 * article's), set where its other properties are edited.
 *
 * Library LB6c-T1, acceptance L6c1-07 / L6c1-08. Decision 60: any document can
 * carry tags, from ONE tag store, through ONE door — PUT
 * /knowledge/documents/:id/tags, for every kind. This is the ONE field that
 * door is drawn with: the document edit form (routes/library/doc.$docId.tsx,
 * opened by Edit Metadata) and the article editor's sidebar
 * (components/kb/EditorSidebar.tsx) both draw it. There is no second copy of
 * the chips.
 *
 * ITS KEY. The door declares write:library.documents (decision 26 — the key
 * that governs creating, renaming and deleting tags too), so the field asks
 * exactly that key and is not drawn without it. In the article editor (opened
 * under write:wiki) a person who holds write:wiki alone sees the body editor
 * without this field, and the save sends no tag write. Recorded on the
 * `documentTagging` and `articleTagging` rows of LIBRARY_CONTROLS.
 *
 * ITS CATALOGUE. The tags it offers are the tenant's own (GET /wiki/tags),
 * read through `useWikiTags()` — the ONE query key the Documents page's tag
 * manager and its Tag filter read too, so the three are one read. Beside the
 * chips it draws the tag manager (TagManager), from the same read: a tag
 * created there appears in the chips (the key is invalidated).
 *
 * NOTHING FAILS OPEN. Until the catalogue is read the field says so; a failed
 * read shows the error with a retry — never "no tags" (LB6T2-REV-04) — and
 * offers no chip, so the form it sits in cannot change the set and sends no
 * tag write. The caller sends a write only when the set changed
 * (`tagSetChanged`), and only for a document whose tags it has read.
 */
import { Loader2, Tag } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { DocumentTagsSchema, type ArticleTagShape, type DocumentTagsWrite } from '@argusai/shared';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TagManager } from '@/components/kb/TagManager';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { useHasPermission } from '@/lib/use-has-permission';
import { useWikiTags, wikiTagsQueryKey } from '@/lib/use-library-documents';

/** The one tag door, every kind of document. */
export function documentTagsPath(documentId: string): string {
  return `/knowledge/documents/${encodeURIComponent(documentId)}/tags`;
}

/**
 * Write a document's whole tag set (PUT /knowledge/documents/:id/tags) and
 * answer the set as the server now stores it. REJECTS on a refusal and on an
 * answer that is not the tag set — never taken as "saved".
 */
export async function saveDocumentTags(documentId: string, tagIds: readonly string[]): Promise<ArticleTagShape[]> {
  const body: DocumentTagsWrite = { tags: [...new Set(tagIds)] };
  const res = await apiClient.put<unknown>(documentTagsPath(documentId), body);
  const parsed = DocumentTagsSchema.safeParse(res);
  if (!parsed.success) throw new Error('The saved tags could not be read.');
  return parsed.data.tags;
}

/**
 * Does `next` differ from the stored set `stored`? Null on either side — the
 * stored set was never read, or the form never offered the field — is "no
 * change": nothing is written over what was not read.
 */
export function tagSetChanged(
  stored: readonly string[] | null | undefined,
  next: readonly string[] | null | undefined,
): boolean {
  if (!stored || !next) return false;
  const a = new Set(stored);
  const b = new Set(next);
  if (a.size !== b.size) return true;
  for (const id of a) if (!b.has(id)) return true;
  return false;
}

export interface DocumentTagsFieldProps {
  /** The ids ticked now (the form's state). */
  selectedTagIds: readonly string[];
  /** Tick or untick one tag. */
  onToggle: (tagId: string) => void;
  /** 'card' — the document edit form's card; 'sidebar' — the article editor's metadata panel. */
  frame: 'card' | 'sidebar';
  disabled?: boolean;
}

export function DocumentTagsField({ selectedTagIds, onToggle, frame, disabled = false }: DocumentTagsFieldProps) {
  // The key PUT /knowledge/documents/:id/tags declares (decision 26 / 60).
  const canTag = useHasPermission('write', 'library.documents');
  const tagsQ = useWikiTags();
  const queryClient = useQueryClient();
  if (!canTag) return null;

  const catalogue = tagsQ.data;
  const onTagsChange = () => {
    void queryClient.invalidateQueries({ queryKey: wikiTagsQueryKey });
  };
  const manager = catalogue ? (
    <TagManager
      tags={catalogue}
      onTagsChange={onTagsChange}
      documentCounts={Object.fromEntries(catalogue.map((t) => [t.id, t.count]))}
    />
  ) : null;

  let body;
  if (tagsQ.isError) {
    body = (
      <div role="alert" data-testid="document-tags-error" className="flex flex-wrap items-center gap-x-2 text-xs text-destructive">
        <span>The tag list could not be loaded.</span>
        <button type="button" className="underline" onClick={() => void tagsQ.refetch()} title="Load the tag list again">
          Retry
        </button>
      </div>
    );
  } else if (!catalogue) {
    body = (
      <p className="flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading the tag list…
      </p>
    );
  } else if (catalogue.length === 0) {
    body = <p className="text-xs text-muted-foreground italic">This library has no tags yet. Add one with the gear.</p>;
  } else {
    body = (
      <div className="flex flex-wrap gap-2">
        {catalogue.map((tag) => {
          const isSelected = selectedTagIds.includes(tag.id);
          return (
            <Badge
              key={tag.id}
              variant="outline"
              role="checkbox"
              aria-checked={isSelected}
              aria-disabled={disabled}
              title={isSelected ? `Remove the tag "${tag.name}"` : `Add the tag "${tag.name}"`}
              className={cn(
                'cursor-pointer transition-all px-3 py-1',
                isSelected
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'hover:bg-muted text-muted-foreground hover:text-foreground',
                disabled && 'pointer-events-none opacity-60',
              )}
              onClick={() => { if (!disabled) onToggle(tag.id); }}
            >
              {tag.name}
            </Badge>
          );
        })}
      </div>
    );
  }

  if (frame === 'card') {
    return (
      <Card data-testid="document-tags-field">
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm">Tags</CardTitle>
          {manager}
        </CardHeader>
        <CardContent>{body}</CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="document-tags-field">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <Tag className="h-3.5 w-3.5" /> Tags
        </h4>
        {manager}
      </div>
      {body}
    </div>
  );
}
